import { useEffect, useState, useRef } from "react";
import { RefreshCw } from "lucide-react";

export const UpdateBanner = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const waitingWorker = useRef(null);
  const refreshing = useRef(false);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;

    const promptUpdate = (worker) => {
      if (!worker) return;
      waitingWorker.current = worker;
      setUpdateAvailable(true);
    };

    const registerSW = async () => {
      try {
        const reg = await navigator.serviceWorker.register("/sw.js");

        // A new worker is already waiting (installed while page was open before)
        if (reg.waiting && navigator.serviceWorker.controller) {
          promptUpdate(reg.waiting);
        }

        reg.addEventListener("updatefound", () => {
          const newWorker = reg.installing;
          if (!newWorker) return;
          newWorker.addEventListener("statechange", () => {
            if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
              promptUpdate(newWorker);
            }
          });
        });

        // Check for updates when the tab regains focus
        const onVisibility = () => {
          if (document.visibilityState === "visible") reg.update().catch(() => {});
        };
        document.addEventListener("visibilitychange", onVisibility);
        return () => document.removeEventListener("visibilitychange", onVisibility);
      } catch (err) {
        if (process.env.NODE_ENV !== "production") {
          console.warn("Service worker registration failed:", err?.message);
        }
      }
    };

    const onControllerChange = () => {
      if (refreshing.current) return;
      refreshing.current = true;
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener("controllerchange", onControllerChange);

    let cleanup;
    if (document.readyState === "complete") {
      registerSW().then((c) => { cleanup = c; });
    } else {
      window.addEventListener("load", () => registerSW().then((c) => { cleanup = c; }), { once: true });
    }

    return () => {
      navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange);
      if (cleanup) cleanup();
    };
  }, []);

  const handleReload = () => {
    if (waitingWorker.current) {
      waitingWorker.current.postMessage({ type: "SKIP_WAITING" });
    } else {
      window.location.reload();
    }
  };

  if (!updateAvailable) return null;

  return (
    <div
      data-testid="update-banner"
      className="fixed inset-x-0 bottom-0 z-[100] flex justify-center px-4 pb-4 pointer-events-none"
    >
      <div className="pointer-events-auto flex w-full max-w-md items-center gap-3 rounded-2xl bg-[#002FA7] px-4 py-3 text-white shadow-2xl ring-1 ring-white/10 animate-in slide-in-from-bottom-4 fade-in duration-300">
        <RefreshCw className="h-5 w-5 shrink-0" aria-hidden="true" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold leading-tight">Nuova versione disponibile</p>
          <p className="text-xs text-white/70 leading-tight">Ricarica per aggiornare l'applicazione.</p>
        </div>
        <button
          type="button"
          data-testid="update-banner-reload-button"
          onClick={handleReload}
          className="shrink-0 rounded-full bg-white px-4 py-1.5 text-sm font-semibold text-[#002FA7] transition-colors hover:bg-white/90 active:scale-95"
        >
          Ricarica
        </button>
      </div>
    </div>
  );
};
